import React, { useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';

import useServiceHook from '@hooks/useServiceHook';
import { ConfirmationBox, InfoBox, WarningBox } from '@components/cards/messageBox';
import { NavBar } from '@components/navigation/NavBar/NavBar';
import CircularSpinner from "@components/spinners/Circular/Circular";
import IdentityVerificationService from '@services/identityVerification/IdentityVerificationService';
import { Country } from '@utils/constants/localisation';
import Button, { ButtonStyle } from '@components/buttons/Button/Button';
import WarningModal from '@components/modal/info/Warning';
// eslint-disable-next-line camelcase
import { dateToISOFormat, formatDateToDD_MMM_YYYY, utcDateTimeExpressedInLocalTimeZone } from '@utils/misc/functions';
import IdentityVerificationCheckDocuments from '@views/DocumentStore/IdentityVerificationCheckDocuments';
import {EnvTag, getEnvTag} from "@utils/AppEnv";

const LoadingSpinner = () => (
    <div data-testid="loading-spinner" style={{ display: 'flex', justifyContent: 'center', margin: '15vh' }}>
        {<CircularSpinner />}
    </div>
)

const VerificationStatusInfo = ({ check }: { check: any }) => {
    // If the verification source is Splend, display the specific checked_by user responsible.
    // Otherwise the source is likely ShuftiPro with no checked_by.
    const verifiedBy = check['source'] === 'Splend' ? check['checked_by'] : check['source'];

    const time = utcDateTimeExpressedInLocalTimeZone(check['initiated_datetime'], undefined, 'short');
    const date = formatDateToDD_MMM_YYYY(check['initiated_datetime']);

    return (
        <InfoBox
            message={
                <>
                    <strong>Identity Last Verified</strong> by {verifiedBy} at {time} on {date}.
                </>
            }
            testId='verified-status-info'
        />
    );
}

const isDateExpired = (expiryDate: string | undefined): boolean => {
    if (!expiryDate) return false;
    const today = dateToISOFormat(new Date());
    // expiryDate is already stored in the same YYYY-MM-DD format
    return expiryDate <= today;
}

export default function IdentityVerification() {
    const navigate = useNavigate();

    const { custId, country } = useParams() as { custId: string, country: Country };

    const [identityVerificationCheck, setIdentityVerificationCheck] = useState(null);
    const [displayErrorMsg, setDisplayErrorMsg] = useState(false)

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const journey: string | null = queryParams.get('journey') == null ? '' : queryParams.get('journey');

    const [loading, error, errMsg, result] = useServiceHook(
        IdentityVerificationService.getCustomerIdentityVerification,
        [custId, country],
        true,
        [],
        undefined,
        [true] // show only latest
    );

    const identityDocumentTypes = ['Driver Licence', 'Passport', 'Credit or Debit Card', 'ID Card', 'Utility Bill',
        'Bank Statement', 'Credit Card Statement', 'Rental Agreement', 'Queensland Driver Authority Card',
        'Medicare Card', 'PCO'];

    if (loading == false && result && result['identity_verification_checks'] && result['identity_verification_checks'].length != 0 && !identityVerificationCheck) {
        setIdentityVerificationCheck(result['identity_verification_checks'][0])
    }

    const modalOnClick = (event: any) => {
        event.preventDefault();
        setDisplayErrorMsg(false);
        window.location.reload();
    };

    const showSkipIdentifyCheckButton = (): boolean => {
        // Determine if we should show the skip identity check button
        // Assume we do show it, unless conditions to hide are met
        
        // Button should only be shown if part of a journey
        // (Dont show if using the page alone)
        if (journey !== 'onboard') {
            return false;
        }
        // It should be hidden in production for both AU and GB, to enforce the ID check
        if (getEnvTag()  === EnvTag.Production) {
            return false;
        }
        return true;
    };

    return (
        <>
            <NavBar pageTitle="Identity Details" />
            {loading && <LoadingSpinner />}

            {
                !loading && result && result['customer_identity_verification_status'] == 'Not Verified' &&
                // This will also occur if the drivers licence is expired but the status would otherwise be verified
                <>
                    {
                        identityVerificationCheck
                            && identityVerificationCheck['driver_licence_expiry_date']
                            && isDateExpired(identityVerificationCheck['driver_licence_expiry_date'])
                            ? (
                                // Expired licence
                                // (might also be Not Verified for other reasons but we'd be unlikely to save the check)
                                <>
                                    <div style={{ marginTop: '2vh' }}>
                                        <WarningBox message={'Drivers Licence has expired. Please update drivers licence.'} />
                                    </div>

                                    <IdentityVerificationCheckDocuments
                                        identityVerificationCheckReferenceId={identityVerificationCheck['identity_verification_check_reference_id']}
                                        documentTypes={identityDocumentTypes}
                                    />
                                </>
                            ) : (
                                <>
                                    <div style={{ marginTop: '2vh' }}>
                                        <WarningBox message={'We don\'t currently have a valid identity record for this customer.'} />
                                    </div>

                                    {
                                        identityVerificationCheck &&
                                        <>
                                            <div style={{ marginTop: '2vh' }}>
                                                {`Last check status: ${identityVerificationCheck['status']}`}
                                            </div>

                                            {
                                                identityVerificationCheck['status'] == 'Submitted' &&
                                                <>
                                                    <div style={{ marginTop: '2vh' }}>
                                                        <div>
                                                            An error occurred while uploading documents for the last ID check.
                                                        </div>
                                                    </div>
                                                </>
                                            }

                                            {
                                                identityVerificationCheck['status'] == 'ID Check Failed' &&
                                                <>
                                                    <div style={{ marginTop: '2vh' }}>
                                                        <div>
                                                            <a href={identityVerificationCheck['vendor_verification_admin_url']} target="_blank" rel="noreferrer">View the identity check</a>
                                                        </div>
                                                        <div>
                                                            (Managers only link)
                                                        </div>
                                                    </div>
                                                </>
                                            }

                                            <div style={{ marginTop: '2vh' }}>
                                                {`The last digital identity verification request has been sent on ${utcDateTimeExpressedInLocalTimeZone(identityVerificationCheck['initiated_datetime'], "long", "long")}.`}
                                            </div>
                                        </>
                                    }
                                </>
                            )
                    }

                    <div style={{ margin: '2.5vh 0' }}>
                        <Button
                            label="Upload Identity Documents"
                            onClickFunc={() => navigate(`/identity-verification/${country}/${custId}/upload`)}
                        />
                    </div>

                    {
                        identityVerificationCheck && identityVerificationCheck['status'] == 'ID Check Failed' &&
                        <div style={{ margin: '2.5vh 0' }}>
                            <Button
                                label="Refresh identity details"
                                onClickFunc={() => { window.location.reload() }}
                            />
                        </div>
                    }
                    
                    {
                        showSkipIdentifyCheckButton() &&
                        <div style={{ margin: '2.5vh 0' }}>
                            <Button
                                label="Skip identity check"
                                testId="skip-identity-check-button"
                                btnType={ButtonStyle.tertiary}
                                onClickFunc={() => navigate(`/payment-method/${country}/${custId}/review?next_activity=onboard`)}
                            />
                        </div>
                    }
                </>

            }

            {
                !loading && result && result['customer_identity_verification_status'] == 'Pending Manual Approval' &&
                <>
                    <div style={{ marginTop: '2vh' }}>
                        <WarningBox message={'There is an identity verification check that requires manual review & approval.'} />
                    </div>

                    <div style={{ marginTop: '2vh' }}>
                        A manager is required to review and reject or approve any identity checks that have been flagged for review.
                    </div>


                    {
                        identityVerificationCheck &&
                        <>
                            <div style={{ marginTop: '2vh' }}>
                                <div>
                                    <a href={identityVerificationCheck['vendor_verification_admin_url']} target="_blank" rel="noreferrer">View the identity check</a>
                                </div>
                                <div>
                                    (Managers only link)
                                </div>
                            </div>
                        </>
                    }
                    
                    <div style={{ marginTop: '2vh' }}>
                        Once the manual review has been approved, please refresh this page.
                    </div>

                    <div style={{ margin: '2.5vh 0' }}>
                        <Button
                            label="Refresh identity details"
                            onClickFunc={() => { window.location.reload() }}
                        />
                    </div>

                    {
                        showSkipIdentifyCheckButton() &&
                        <div style={{ margin: '2.5vh 0' }}>
                            <Button
                                label="Skip identity check"
                                testId="skip-identity-check-button"
                                btnType={ButtonStyle.tertiary}
                                onClickFunc={() => navigate(`/payment-method/${country}/${custId}/review?next_activity=onboard`)}
                            />
                        </div>
                    }

                    <div style={{ margin: '2.5vh 0' }}>
                        <Button
                            label="Upload Identity Documents"
                            btnType={ButtonStyle.tertiary}
                            onClickFunc={() => navigate(`/identity-verification/${country}/${custId}/upload`)}
                        />
                    </div>
                </>
            }

            {
                !loading && result && result['customer_identity_verification_status'] == 'Pending' && identityVerificationCheck &&
                <>
                    <div style={{ marginTop: '2vh' }}>
                        <ConfirmationBox message={
                            `A digital identity verification request has been sent to customer on ${utcDateTimeExpressedInLocalTimeZone(identityVerificationCheck['initiated_datetime'], "long", "long")}.`
                        } />
                    </div>

                    <div style={{ marginTop: '2vh' }}>
                        Please assist the customer through their identity check. Ask them to start the process from the email they should have received in their inbox.
                    </div>

                    <div style={{ marginTop: '2vh' }}>
                        <a href="https://splendptyltd.atlassian.net/servicedesk/customer/portal/2/topic/d3d869f4-195b-4c15-8696-d53409a0e005/article/2834792615" target="_blank" rel="noreferrer">View detailed instructions here.</a>
                    </div>

                    <div style={{ marginTop: '2vh' }}>
                        Once customer has completed the identity verification, please refresh this page.
                    </div>

                    <div style={{ margin: '2.5vh 0' }}>
                        <Button
                            label="Refresh identity details"
                            onClickFunc={() => { window.location.reload() }}
                        />
                    </div>

                    {
                        showSkipIdentifyCheckButton() &&
                        <div style={{ margin: '2.5vh 0' }}>
                            <Button
                                label="Skip identity check"
                                testId="skip-identity-check-button"
                                btnType={ButtonStyle.tertiary}
                                onClickFunc={() => navigate(`/payment-method/${country}/${custId}/review?next_activity=onboard`)}
                            />
                        </div>
                    }

                    <div style={{ margin: '2.5vh 0' }}>
                        <Button
                            label="Upload Identity Documents"
                            btnType={ButtonStyle.tertiary}
                            onClickFunc={() => navigate(`/identity-verification/${country}/${custId}/upload`)}
                        />
                    </div>
                </>
            }


            {
                !loading && result && result['customer_identity_verification_status'] == 'Verified' && identityVerificationCheck &&
                <>
                    <div style={{ marginTop: '2vh' }}>
                        <VerificationStatusInfo check={identityVerificationCheck} />
                    </div>

                    <IdentityVerificationCheckDocuments identityVerificationCheckReferenceId={identityVerificationCheck['identity_verification_check_reference_id']} documentTypes={identityDocumentTypes} />

                    {
                        journey === 'onboard' &&
                        <div style={{ margin: '2.5vh 0' }}>
                            <Button
                                label="Next"
                                onClickFunc={() => navigate(`/payment-method/${country}/${custId}/review?next_activity=onboard`)}
                            />
                        </div>
                    }

                    <div style={{ margin: '2.5vh 0' }}>
                        <Button
                            label="Re-upload Identity Documents"
                            btnType={ButtonStyle.tertiary}
                            onClickFunc={() => navigate(`/identity-verification/${country}/${custId}/upload`)}
                        />
                    </div>
                </>
            }

            {
                displayErrorMsg &&
                <WarningModal
                    message={'Something went wrong'}
                    additionalInfo={'We’re sorry, we were unable to process your request. Try refreshing the page or contact IT Support for assistance'}
                    onClickClose={modalOnClick}
                />
            }
        </>
    )
}